import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { User, Phone, Mail, Calendar, CheckCircle, AlertCircle, Gift, ArrowRight } from 'lucide-react';
import { customerService } from '../../services/customerService';
import { companyService } from '../../services/companyService';
import { getPublicAvailableRewards } from '../../services/rewardsService';
import { formatters } from '../../utils/formatters';
import { validators } from '../../utils/validators';
import LoadingSpinner from '../common/LoadingSpinner';
import CepField from '../common/CepField';

const PublicCustomerRegistration = () => {
  const { companyId } = useParams();
  
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [welcomeBonus, setWelcomeBonus] = useState(null);
  const [customerPoints, setCustomerPoints] = useState(0);
  const [availableRewards, setAvailableRewards] = useState([]);
  const [showRewards, setShowRewards] = useState(false);
  const [loadingRewards, setLoadingRewards] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    document: '',
    phone: '',
    email: '',
    birthDate: '',
    referralCode: '',
    // Campos de endereço opcionais
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: ''
  });
  
  const [errors, setErrors] = useState({});

  // Carregar informações da empresa
  useEffect(() => {
    const loadCompany = async () => {
      try {
        if (!companyId) {
          setError('ID da empresa não fornecido');
          setLoading(false);
          return;
        }

        const companyData = await companyService.getById(companyId);
        setCompany(companyData);
      } catch (err) {
        console.error('Erro detalhado ao carregar empresa:', {
          error: err,
          message: err.message,
          code: err.code,
          companyId: companyId
        });
        
        if (err.code === 'permission-denied') {
          setError('Acesso negado. Verifique as regras de segurança do Firestore.');
        } else if (err.message.includes('not found')) {
          setError('Empresa não encontrada. Verifique se o link está correto.');
        } else {
          setError(`Erro ao carregar dados da empresa: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    loadCompany();
  }, [companyId]);

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return value.trim().length >= 2 ? '' : 'Nome deve ter pelo menos 2 caracteres';
      case 'document':
        if (!validators.isValidCpfOrCnpj(value)) {
          const docType = validators.getDocumentType(value);
          if (docType === 'unknown') {
            return 'CPF ou CNPJ inválido';
          } else {
            return `${docType === 'cpf' ? 'CPF' : 'CNPJ'} inválido`;
          }
        }
        return '';
      case 'phone':
        const cleanPhone = value.replace(/\D/g, '');
        return cleanPhone.length >= 10 ? '' : 'Telefone deve ter pelo menos 10 dígitos';
      case 'email':
        return !value || validators.isValidEmail(value) ? '' : 'Email inválido';
      case 'referralCode':
        return !value || value.trim().length >= 3 ? '' : 'Código de indicação deve ter pelo menos 3 caracteres';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // Aplicar formatação
    if (name === 'document') {
      formattedValue = formatters.formatCpfOrCnpj(value);
    } else if (name === 'phone') {
      formattedValue = formatters.formatPhone(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));

    // Validar campo
    const fieldError = validateField(name, formattedValue);
    setErrors(prev => ({
      ...prev,
      [name]: fieldError
    }));
  };

  const handleCepChange = useCallback((cepValue) => {
    setFormData(prev => ({ ...prev, cep: cepValue }));
  }, []);

  const handleAddressChange = useCallback((addressData) => {
    // Preencher automaticamente os campos de endereço com os dados do CEP
    setFormData(prev => ({
      ...prev,
      street: addressData.endereco || prev.street,
      neighborhood: addressData.bairro || prev.neighborhood,
      city: addressData.cidade || prev.city,
      state: addressData.estado || prev.state
    }));
  }, []);

  // Função para buscar prêmios disponíveis após o cadastro
  const loadAvailableRewards = async (points = 0) => {
    setLoadingRewards(true);
    try {
      const rewards = await getPublicAvailableRewards(companyId, points);
      setAvailableRewards(rewards);
      setCustomerPoints(points);
    } catch (err) {
      console.error('Erro ao carregar prêmios:', err);
      // Em caso de erro, continua sem os prêmios
      setAvailableRewards([]);
    } finally {
      setLoadingRewards(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar todos os campos
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      if (key !== 'email' && key !== 'birthDate') { // Campos obrigatórios
        const error = validateField(key, formData[key]);
        if (error) newErrors[key] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const result = await customerService.createPublic({
        ...formData,
        cpf: formatters.cleanCpfOrCnpj(formData.document),
        phone: formData.phone.replace(/\D/g, ''),
        companyId
      });

      setWelcomeBonus(result.welcomeBonusGiven ? result.welcomeBonusAmount : null);
      setSuccess(true);
      
      // Buscar prêmios disponíveis após o cadastro
      const totalPoints = result.welcomeBonusGiven ? result.welcomeBonusAmount : 0;
      await loadAvailableRewards(totalPoints);
    } catch (err) {
      console.error('Erro ao cadastrar cliente:', err);
      if (err.message.includes('CPF já cadastrado') || err.message.includes('CNPJ já cadastrado') || err.message.includes('documento já cadastrado')) {
        setError('Este documento já está cadastrado no sistema.');
      } else {
        setError('Erro ao realizar cadastro. Tente novamente.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && !company) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">Erro</h1>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    // Se está mostrando os prêmios
    if (showRewards) {
      return (
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-6xl mx-auto px-4">
            <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
              <div className="text-center mb-8">
                <Gift className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Prêmios Disponíveis</h1>
                <p className="text-gray-600 mb-2">
                  Veja todos os prêmios que você pode conquistar na {company?.name}!
                </p>
                {customerPoints > 0 && (
                  <p className="text-sm text-green-600 font-medium">
                    Você tem {customerPoints} pontos disponíveis
                  </p>
                )}
              </div>

              {loadingRewards ? (
                <div className="text-center py-8">
                  <LoadingSpinner />
                  <p className="text-gray-600 mt-2">Carregando prêmios...</p>
                </div>
              ) : availableRewards.length === 0 ? (
                <div className="text-center py-8">
                  <Gift className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">Nenhum prêmio disponível no momento.</p>
                  <p className="text-sm text-gray-500">Continue acumulando pontos para futuros prêmios!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 mb-8">
                  {availableRewards.map((reward) => (
                    <div
                      key={reward.id}
                      className={`border rounded-lg p-6 transition-all ${
                        reward.canRedeem
                          ? 'border-green-200 bg-green-50'
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      {/* Imagem do Prêmio */}
                      {reward.imageUrl && (
                        <div className="mb-4">
                          <img
                            src={reward.imageUrl}
                            alt={reward.name}
                            className="w-full h-40 object-cover rounded-lg"
                          />
                        </div>
                      )}
                      
                      <h3 className="font-semibold text-gray-900 mb-2">{reward.name}</h3>
                      <p className="text-sm text-gray-600 mb-4">{reward.description}</p>
                      
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-lg font-bold text-blue-600">
                          {reward.pointsRequired} pontos
                        </span>
                        {reward.canRedeem ? (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                            Você pode resgatar!
                          </span>
                        ) : (
                          <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                            Faltam {reward.pointsNeeded} pontos
                          </span>
                        )}
                      </div>
                      
                      {reward.value && (
                        <p className="text-sm text-gray-500">
                          {reward.type === 'cashback' ? `Valor: R$ ${reward.value}` : reward.value}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="text-center">
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => setShowRewards(false)}
                    className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Voltar
                  </button>
                  <button
                    onClick={() => window.close()}
                    className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Tela inicial de sucesso com opção de ver prêmios
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900 mb-2">Cadastro Realizado!</h1>
            <p className="text-gray-600 mb-4">
              Parabéns! Você foi cadastrado com sucesso no programa de fidelidade da {company?.name}.
            </p>
            {welcomeBonus !== null && welcomeBonus > 0 ? (
              <p className="text-sm text-green-600 font-medium mb-6">
                🎉 Você ganhou {welcomeBonus} pontos de bônus de boas-vindas!
              </p>
            ) : (
              <p className="text-sm text-blue-600 mb-6">
                Agora você pode acumular pontos a cada compra!
              </p>
            )}
            
            <div className="space-y-3">
              {!loadingRewards && (
                <button
                  onClick={() => setShowRewards(true)}
                  className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Gift size={20} />
                  {availableRewards.length > 0 ? 'Ver Prêmios Disponíveis' : 'Ver Catálogo de Prêmios'}
                  <ArrowRight size={16} />
                </button>
              )}
              
              <button
                onClick={() => window.close()}
                className="w-full bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-md mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-8">
            {company?.logo && (
              <img 
                src={company.logo} 
                alt={company.name}
                className="w-16 h-16 mx-auto mb-4 object-contain"
              />
            )}
            <h1 className="text-2xl font-bold text-gray-900">
              Cadastre-se
            </h1>
            <p className="text-gray-600 mt-2">
              Programa de Fidelidade
            </p>
            <p className="font-medium text-blue-600">
              {company?.name}
            </p>
            {company?.description && (
              <p className="text-sm text-gray-500 mt-2">
                {company.description}
              </p>
            )}
          </div>

          {/* Benefícios */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-900 mb-2">Seus benefícios:</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              {company?.welcomeBonus > 0 && (
                <li>• {company.welcomeBonus} pontos de bônus de boas-vindas</li>
              )}
              <li>• {company?.pointsPerReal || 1} ponto para cada R$ 1,00 gasto</li>
              <li>• Resgate prêmios a partir de {company?.minimumRedemption || 50} pontos</li>
            </ul>
          </div>

          {/* Formulário */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                Nome Completo *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Seu nome completo"
                required
              />
              {errors.name && (
                <p className="text-red-600 text-sm mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CPF/CNPJ *
              </label>
              <input
                type="text"
                name="document"
                value={formData.document}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.document ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                maxLength="18"
                required
              />
              {errors.document && (
                <p className="text-red-600 text-sm mt-1">{errors.document}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Digite seu CPF (pessoa física) ou CNPJ (pessoa jurídica)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="w-4 h-4 inline mr-1" />
                Telefone *
              </label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.phone ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="(00) 00000-0000"
                maxLength="15"
                required
              />
              {errors.phone && (
                <p className="text-red-600 text-sm mt-1">{errors.phone}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Email (opcional)
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.email ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="seu@email.com"
              />
              {errors.email && (
                <p className="text-red-600 text-sm mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Data de Nascimento (opcional)
              </label>
              <input
                type="date"
                name="birthDate"
                value={formData.birthDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Gift className="w-4 h-4 inline mr-1" />
                Código de Indicação (opcional)
              </label>
              <input
                type="text"
                name="referralCode"
                value={formData.referralCode}
                onChange={handleChange}
                placeholder="Digite o código de quem te indicou"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.referralCode ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.referralCode && (
                <p className="mt-1 text-sm text-red-600">{errors.referralCode}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Se alguém te indicou, digite o código aqui para que vocês ganhem pontos extras!
              </p>
            </div>

            {/* Seção de Endereço */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-4">Endereço (opcional)</h3>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CEP
                  </label>
                  <CepField
                    value={formData.cep}
                    onChange={handleCepChange}
                    onAddressChange={handleAddressChange}
                    placeholder="00000-000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número
                  </label>
                  <input
                    type="text"
                    name="number"
                    value={formData.number}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="123"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rua/Logradouro
                </label>
                <input
                  type="text"
                  name="street"
                  value={formData.street}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nome da rua, avenida, etc."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bairro
                  </label>
                  <input
                    type="text"
                    name="neighborhood"
                    value={formData.neighborhood}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Bairro"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cidade
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Cidade"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estado
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="UF"
                    maxLength="2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Complemento
                  </label>
                  <input
                    type="text"
                    name="complement"
                    value={formData.complement}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Apartamento, bloco, etc."
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Cadastrando...' : 'Completar Cadastro'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Ao se cadastrar, você concorda com nossos termos de uso e política de privacidade.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicCustomerRegistration;
